import { __assign, __values } from "tslib";
import { UPlotConfigBuilder } from '../uPlot/config/UPlotConfigBuilder';
import { FieldType, formattedValueToString, getFieldColorModeForField, getFieldDisplayName, getFieldSeriesColor, MutableDataFrame, VizOrientation, } from '@grafana/data';
import { BarValueVisibility, defaultBarChartFieldConfig } from './types';
import { AxisPlacement, ScaleDirection, ScaleDistribution, ScaleOrientation } from '../uPlot/config';
import { getConfig } from './bars';
import { FIXED_UNIT } from '../GraphNG/GraphNG';
/** @alpha */
export function preparePlotConfigBuilder(data, theme, _a) {
    var _b, _c;
    var orientation = _a.orientation, showValue = _a.showValue, groupWidth = _a.groupWidth, barWidth = _a.barWidth;
    var builder = new UPlotConfigBuilder();
    // bar orientation -> x scale orientation & direction
    var xOri = ScaleOrientation.Vertical;
    var xDir = ScaleDirection.Down;
    var yOri = ScaleOrientation.Horizontal;
    var yDir = ScaleDirection.Right;
    if (orientation === VizOrientation.Vertical) {
        xOri = ScaleOrientation.Horizontal;
        xDir = ScaleDirection.Right;
        yOri = ScaleOrientation.Vertical;
        yDir = ScaleDirection.Up;
    }
    var formatValue = showValue !== BarValueVisibility.Never
        ? function (seriesIdx, value) { return formattedValueToString(data.fields[seriesIdx].display(value)); }
        : undefined;
    // Use bar width when only one field
    if (data.fields.length === 2) {
        groupWidth = barWidth;
        barWidth = 1;
    }
    var opts = {
        xOri: xOri,
        xDir: xDir,
        groupWidth: groupWidth,
        barWidth: barWidth,
        formatValue: formatValue,
        onHover: function (seriesIdx, valueIdx) {
            console.log('hover', { seriesIdx: seriesIdx, valueIdx: valueIdx });
        },
        onLeave: function (seriesIdx, valueIdx) {
            console.log('leave', { seriesIdx: seriesIdx, valueIdx: valueIdx });
        },
    };
    var config = getConfig(opts);
    builder.addHook('init', config.init);
    builder.addHook('drawClear', config.drawClear);
    builder.addHook('setCursor', config.setCursor);
    builder.setCursor(config.cursor);
    builder.setSelect(config.select);
    builder.addScale({
        scaleKey: 'x',
        isTime: false,
        distribution: ScaleDistribution.Ordinal,
        orientation: xOri,
        direction: xDir,
    });
    builder.addAxis({
        scaleKey: 'x',
        isTime: false,
        placement: xOri === 0 ? AxisPlacement.Bottom : AxisPlacement.Left,
        splits: config.xSplits,
        values: config.xValues,
        grid: false,
        ticks: false,
        gap: 15,
        theme: theme,
    });
    var seriesIndex = 0;
    var _loop_1 = function (i) {
        var field = data.fields[i];
        field.state.seriesIndex = seriesIndex++;
        var customConfig = __assign(__assign({}, defaultBarChartFieldConfig), field.config.custom);
        var scaleKey = field.config.unit || FIXED_UNIT;
        var colorMode = getFieldColorModeForField(field);
        var scaleColor = getFieldSeriesColor(field, theme);
        var seriesColor = scaleColor.color;
        builder.addSeries({
            scaleKey: scaleKey,
            pxAlign: false,
            lineWidth: customConfig.lineWidth,
            lineColor: seriesColor,
            //lineStyle: customConfig.lineStyle,
            fillOpacity: customConfig.fillOpacity,
            theme: theme,
            colorMode: colorMode,
            pathBuilder: config.drawBars,
            pointsBuilder: config.drawPoints,
            show: !((_b = customConfig.hideFrom) === null || _b === void 0 ? void 0 : _b.graph),
            gradientMode: customConfig.gradientMode,
            thresholds: field.config.thresholds,
            // The following properties are not used in the uPlot config, but are utilized as transport for legend config
            dataFrameFieldIndex: {
                fieldIndex: i,
                frameIndex: 0,
            },
            fieldName: getFieldDisplayName(field, data),
            hideInLegend: (_c = customConfig.hideFrom) === null || _c === void 0 ? void 0 : _c.legend,
        });
        // The builder will manage unique scaleKeys and combine where appropriate
        builder.addScale({
            scaleKey: scaleKey,
            min: field.config.min,
            max: field.config.max,
            softMin: customConfig.axisSoftMin,
            softMax: customConfig.axisSoftMax,
            orientation: yOri,
            direction: yDir,
        });
        if (customConfig.axisPlacement !== AxisPlacement.Hidden) {
            var placement = customConfig.axisPlacement;
            if (!placement || placement === AxisPlacement.Auto) {
                placement = AxisPlacement.Left;
            }
            if (xOri === 1) {
                if (placement === AxisPlacement.Left) {
                    placement = AxisPlacement.Bottom;
                }
                if (placement === AxisPlacement.Right) {
                    placement = AxisPlacement.Top;
                }
            }
            builder.addAxis({
                scaleKey: scaleKey,
                label: customConfig.axisLabel,
                size: customConfig.axisWidth,
                placement: placement,
                formatValue: function (v) { return formattedValueToString(field.display(v)); },
                theme: theme,
            });
        }
    };
    // iterate the y values
    for (var i = 1; i < data.fields.length; i++) {
        _loop_1(i);
    }
    return builder;
}
/** @internal */
export function preparePlotFrame(data) {
    var e_1, _a;
    var firstFrame = data[0];
    var firstString = firstFrame.fields.find(function (f) { return f.type === FieldType.string; });
    if (!firstString) {
        throw new Error('No string field in DF');
    }
    var resultFrame = new MutableDataFrame();
    resultFrame.addField(firstString);
    try {
        for (var _b = __values(firstFrame.fields), _c = _b.next(); !_c.done; _c = _b.next()) {
            var f = _c.value;
            if (f.type === FieldType.number) {
                resultFrame.addField(f);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return resultFrame;
}
//# sourceMappingURL=utils.js.map